import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import _ from "lodash";
import { EGButton } from "../../MainComponents/Button/EGButton/EGButton";
import { getListGroupEAM } from "../../../apis/apiEAM";
import { GeneralIconHeader } from "../../Title/GeneralHeader";
import { useTranslation } from "react-i18next";
import { EGPaginator } from "../../Paginator/Paginator";
import { alpha, Box, CardMedia, InputBase, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { SearchIcon2 } from "../../Icon";
import { EGIconButton } from "../../MainComponents/Button/IconButton/IconButton";
import Etiquin from "../../../assets/images/Ethiquin/ethiquin_no_data.svg";

export const SegmentationList = () => {
  const { gTheme } = useSelector((state) => state.theme);
  const navigate = useNavigate();
  const theme = useTheme();
  const [allSegments, setAllSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [t] = useTranslation("users");
  const [search, setSearch] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  // States for paginator
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const filteredSegments = allSegments.filter((item) =>
    item.name?.toLowerCase().includes(search.toLowerCase())
  );
  const totalGroups = filteredSegments.length;
  const currentOffset = (page - 1) * limit;
  const listSegments = filteredSegments.slice(currentOffset, currentOffset + limit);

  const fetchData = () => {
    setLoading(true);
    getListGroupEAM()
      .then((resp) => {
        setAllSegments(resp.response || []);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchData();
  }, []);

    const handlePageChange = (event, newPage) => {
      setPage(newPage);
    };
  
    const handleLimitChange = (e) => {
      const newLimit = Number(e.target.value);
      setLimit(newLimit);
      setPage(1); // Reset to first page when limit changes
    };

    const showingTo = currentOffset + listSegments.length;

  const breadcrumbItems = [
    { label: t("Groups.home"), route: "/" },
    { label: t("Groups.users"), route: "/users" },
    { label: t("Groups.groups") },
  ];

  return (
    <Box id="settings" className={`${gTheme === "light" ? "" : "dark-mode"}`}>
      <Box
        className="dyTheme1 dyBorder1 rounded p-3 p-md-3 mt-20"
        sx={{
          p: { xs: 1.5, lg: '10px 20px' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', lg: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, lg: 0 }
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <GeneralIconHeader
            title={t("Groups.groups")}
            text={t("Groups.manage_create_user_groups")}
            icon="outline_people"
            breadcrumbItems={breadcrumbItems}
          />
        </Box>
      </Box>

      {/* Table Content */}
      <Box
        className="dyTheme1 dyBorder1 rounded p-3 p-md-4 mt-20"
        sx={{
          mt: -0.3,
          mb: 5,
          pt: { xs: 2, sm: 3 },
          px: 3,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "flex-end",
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1.5,
            pb: 2,
            mb: 2,
            borderBottom: `1px solid ${theme.palette.blueGray200.main}`,
          }}
        >
          <Paper
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: 6,
              height: 37,
              width: { xs: "100%", sm: 320 },
              border: `1px solid ${theme.palette.blueGray200.main}`,
              boxShadow: isSearchFocused
                ? `0 0 4px -2px ${alpha(theme.palette.common.black, 0.8)}`
                : `0 0 3px -3px ${alpha(theme.palette.common.black, 0.8)}`,
              overflow: "hidden",
              p: "2px 4px",
              transition: "box-shadow 0.3s ease-in-out",
            }}
          >
            <SearchIcon2
              style={{ marginLeft: 5 }}
              size={18}
              color={theme.palette.blueGray500.main}
            />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder={t("Groups.searchbar_placeholder")}
              inputProps={{ "aria-label": "search groups" }}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
            />
            {search.trim() !== "" && (
              <EGIconButton
                iconName="close_icon"
                onClick={() => handleSearchChange("")}
                iconColorHover="black"
              />
            )}
          </Paper>
          <EGButton
            color="primary"
            iconName="add_circle"
            iconColor="white"
            iconSize={20}
            label={t("Groups.create_group")}
            onClick={() => navigate("/users/segments/new")}
          />
        </Box>
        {!loading && filteredSegments.length === 0 ? (
          <Stack
            sx={{
              height: "50vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CardMedia
              component="img"
              image={Etiquin}
              alt=""
              sx={{ height: "45%", width: "45%", objectFit: "contain" }}
            />
            <Typography
              variant="h5"
              textAlign="center"
              color="secondary"
              mt={1.5}
            >
              {t("Groups.no_results_found")}...
            </Typography>
          </Stack>
        ) : (
          <TableContainer
            sx={{
              overflowY: "auto",
              overflowX: { xs: "auto", xl: "hidden" },
              border: `1px solid ${alpha(theme.palette.blueGray50.main, 0.3)}`,
              borderTopLeftRadius: "6px",
              borderTopRightRadius: "6px",
            }}
          >
            <Table stickyHeader aria-label="groups table list" size="small">
              <TableHead>
                <TableRow>
                  {[
                    { id: "name", label: t("Groups.group_name"), align: "left" },
                    { id: "created_at", label: t("Groups.creation_date"), align: "left" },
                    { id: "users_count", label: t("Groups.users_count"), align: "left" },
                    { id: "is_active", label: t("Groups.is_active"), align: "left" },
                    { id: "actions", label: "", align: "center" },
                  ].map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        py: 1.25,
                        backgroundColor: theme.palette.blueGray50.main,
                      }}
                    >
                      <Typography variant="h6">{column.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {Array.isArray(listSegments) &&
                  listSegments.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      onClick={() => navigate(`/users/segments/${item.id}`)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          boxShadow: theme.shadows[1],
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: 12 }}>
                        <Typography variant="body2" fontSize={12}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                        <Typography variant="body2" fontSize={12}>
                          {item.created_at ? item.created_at.split("T")[0] : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <Typography variant="body2" fontSize={12}>
                          {item.users ? item.users.length : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <Typography variant="body2" fontSize={12}>
                          {item.is_active === true ? t("Groups.yes") : "No"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <EGIconButton
                            title={t("Groups.edit")}
                            iconName="outline_edit"
                            iconColor="primary"
                            iconColorHover="secondary"
                            iconSize={20}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/users/segments/${item.id}`);
                            }}
                          />
                          <EGIconButton
                            title={t("Groups.delete")}
                            iconName="trash_can"
                            iconColor="error"
                            iconColorHover="error"
                            iconSize={20}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Stack
          sx={{
            mt: 3,
            px: { md: 3 },
            justifyContent: { xs: "center", md: "space-between" },
            flexFlow: "row wrap",
          }}
          spacing={1}
        >
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "space-around",
              flexFlow: "row nowrap",
            }}
          >
            <Typography variant="body2" fontSize={11} mr={0.5}>
              {t("Groups.groups_per_page")}:
            </Typography>
            <select
              style={{ border: "none", fontSize: "11px" }}
              name="limit"
              value={limit}
              onChange={handleLimitChange}
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </Stack>

          <EGPaginator
            onChange={handlePageChange}
            count={Math.ceil(totalGroups / limit)}
            page={page}
          />

          <Typography variant="body2" fontSize={11}>
            {`${t("Groups.results")}: ${
              totalGroups === 0 ? 0 : currentOffset + 1
            } ${t("Groups.to")} ${showingTo} ${t("Groups.of")} ${totalGroups}`}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};
